import { createFileRoute, Link } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { DatePicker } from '@/components/common/DatePicker'
import { Icon } from '@/components/icons'
import { Button, Checkbox, Input, Label, Textarea } from '@/components/ui'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { BILL_LOCATION_LABELS, BILL_LOCATION_VALUES } from '@/constants/bill-location'
import { useDefaultLocation } from '@/hooks/useDefaultLocation'
import { useInitialLocationFilter } from '@/hooks/useInitialLocationFilter'
import { useLocale, useTranslations } from '@/hooks/useTranslations'
import {
  useCreateTempWorker,
  useRecordTempWorkSession,
  useTempWorkerList,
  useTempWorkSessions,
  useUpdateTempWorker,
} from '@/modules/temp-worker/services/tempWorker'
import { getApiErrorMessage } from '@/utils/apiErrorMessage'
import { formatPrice } from '@/utils/number'
import { toast } from '@/components/common/Toast'
import { cn } from '@/lib/utils'
import { WorkerNameCombobox } from '@/components/temp-workers/WorkerNameCombobox'
import { BulkEditWorkDateDialog } from '@/components/pages/Admin/TempWorkers/BulkEditWorkDateDialog'
import { TempWorkSessionRowActions } from '@/components/pages/Admin/TempWorkers/TempWorkSessionRowActions'
import { isTempWorkSessionEditable } from '@/components/pages/Admin/TempWorkers/tempWorkSessionEditable'
import type { TempWorkSession } from '@/modules/temp-worker/services/tempWorker'

export const Route = createFileRoute('/(dashboard)/admin/temp-workers/')({
  component: TempWorkersPage,
})

/** `YYYY-MM-DD` → local calendar date at noon (stable for API + DatePicker). */
function parseWorkDateString(iso: string): Date | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim())
  if (!m) return undefined
  const y = Number(m[1])
  const mo = Number(m[2])
  const d = Number(m[3])
  if (!y || mo < 1 || mo > 12 || d < 1 || d > 31) return undefined
  return new Date(y, mo - 1, d, 12, 0, 0, 0)
}

function toWorkDateString(d: Date): string {
  const y = d.getFullYear()
  const mo = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${mo}-${day}`
}

function avatarInitial(name: string) {
  const s = name.trim()
  if (!s) return '?'
  const ch = Array.from(s)[0]
  return ch?.toUpperCase() ?? '?'
}

function SectionTitle({ accent, children }: { accent: 'blue' | 'slate'; children: React.ReactNode }) {
  return (
    <div
      className={cn(
        'flex items-center gap-3 border-l-4 py-0.5 pl-3',
        accent === 'blue' ? 'border-blue-600' : 'border-slate-800',
      )}
    >
      <h2 className="text-lg font-semibold tracking-tight text-foreground">{children}</h2>
    </div>
  )
}

function CardShell({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <div
      className={cn(
        'rounded-xl border border-border/60 bg-card shadow-sm shadow-black/5',
        className,
      )}
    >
      {children}
    </div>
  )
}

function PrimaryBarButton({
  className,
  children,
  icon,
  type = 'button',
  ...props
}: React.ComponentProps<typeof Button> & { icon: string }) {
  return (
    <Button
      type={type}
      className={cn(
        'h-11 w-full gap-2 rounded-lg bg-slate-800 text-white hover:bg-slate-900 dark:bg-slate-800 dark:hover:bg-slate-900',
        className,
      )}
      {...props}
    >
      <Icon icon={icon} className="size-5 shrink-0" />
      {children}
    </Button>
  )
}


function TempWorkersPage() {
  const t = useTranslations('temp_workers')
  const tButtons = useTranslations('buttons')
  const locale = useLocale()
  const [workDate, setWorkDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [locationFilter, setLocationFilter] = useInitialLocationFilter()
  const { defaultLocation, isLoading: isDefaultLocationLoading } = useDefaultLocation()

  const sessionsParams = useMemo(
    () => ({
      work_date: workDate,
      ...(locationFilter ? { location: locationFilter } : {}),
    }),
    [workDate, locationFilter],
  )

  const [masterPage, setMasterPage] = useState(1)
  const [masterSearch, setMasterSearch] = useState('')
  const [masterSearchInput, setMasterSearchInput] = useState('')

  const { data: workersForCheckIn } = useTempWorkerList({ limit: 500, page: 1 })
  const masterListParams = useMemo(
    () => ({
      include_inactive: true,
      page: masterPage,
      limit: 10,
      ...(masterSearch.trim() ? { search: masterSearch.trim() } : {}),
    }),
    [masterPage, masterSearch],
  )
  const {
    data: workersRes,
    isLoading: workersLoading,
    refetch: refetchWorkers,
  } = useTempWorkerList(masterListParams)

  useEffect(() => {
    setMasterPage(1)
  }, [masterSearch])
  const { data: sessionsRes, isLoading: sessionsLoading, refetch: refetchSessions } =
    useTempWorkSessions(sessionsParams)
  const $recordWork = useRecordTempWorkSession()
  const $createWorker = useCreateTempWorker()
  const $updateWorker = useUpdateTempWorker()

  const activeWorkers = useMemo(() => workersForCheckIn?.data ?? [], [workersForCheckIn])
  const masterRows = workersRes?.data ?? []
  const masterTotalPages = workersRes?.meta?.totalPages ?? 1
  const masterCurrentPage = workersRes?.meta?.currentPage ?? masterPage

  const [recordName, setRecordName] = useState('')
  const [recordLocation, setRecordLocation] = useState<string>('')
  const [recordPay, setRecordPay] = useState('')
  const [recordNote, setRecordNote] = useState('')

  useEffect(() => {
    if (isDefaultLocationLoading || recordLocation) return
    if (defaultLocation) {
      setRecordLocation(defaultLocation)
    }
  }, [defaultLocation, isDefaultLocationLoading, recordLocation])

  const [newMasterName, setNewMasterName] = useState('')
  const [newMasterPhone, setNewMasterPhone] = useState('')
  const [newMasterPosition, setNewMasterPosition] = useState('')

  const [selectedSessionIds, setSelectedSessionIds] = useState<Set<string>>(() => new Set())
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false)
  const [bulkDialogSessions, setBulkDialogSessions] = useState<TempWorkSession[]>([])

  const selectedMasterId = useMemo(() => {
    const match = activeWorkers.find((w) => w.name === recordName.trim())
    return match?.id
  }, [activeWorkers, recordName])

  const onRecordWork = async (e: React.FormEvent) => {
    e.preventDefault()
    const name = recordName.trim()
    if (!name) {
      toast.toastFailed(t('need_name'))
      return
    }
    const n = parseFloat(recordPay)
    if (Number.isNaN(n) || n < 0) {
      toast.toastFailed(t('invalid_pay'))
      return
    }
    try {
      await $recordWork.mutateAsync({
        work_date: workDate,
        pay_baht: n,
        temp_worker_id: selectedMasterId,
        ...(!selectedMasterId ? { worker_name: name } : {}),
        ...(recordLocation ? { location: recordLocation } : {}),
        ...(recordNote.trim() ? { note: recordNote.trim() } : {}),
      })
      toast.toastSuccess(t('record_work_ok'))
      setRecordName('')
      setRecordPay('')
      setRecordNote('')
      refetchSessions()
    } catch (err: any) {
      toast.toastFailed(getApiErrorMessage(err, t('record_work_fail')))
    }
  }

  const onAddMaster = async (e: React.FormEvent) => {
    e.preventDefault()
    const name = newMasterName.trim()
    if (!name) {
      toast.toastFailed(t('need_name'))
      return
    }
    try {
      await $createWorker.mutateAsync({
        name,
        phone: newMasterPhone.trim() || undefined,
        position: newMasterPosition.trim() || undefined,
      })
      toast.toastSuccess(t('master_added'))
      setNewMasterName('')
      setNewMasterPhone('')
      setNewMasterPosition('')
      refetchWorkers()
    } catch (err: any) {
      toast.toastFailed(err?.message || t('master_add_fail'))
    }
  }

  const recordedRows =
    sessionsRes?.data.sessions ?? sessionsRes?.data.closed ?? []

  const paidThroughByWorkerId = useMemo(() => {
    const map = new Map<string, string | null>()
    for (const worker of activeWorkers) {
      map.set(worker.id, worker.paid_through_work_date ?? null)
    }
    return map
  }, [activeWorkers])

  const getPaidThrough = (session: TempWorkSession) =>
    session.temp_worker_id
      ? paidThroughByWorkerId.get(session.temp_worker_id) ?? null
      : null

  const editableRows = useMemo(
    () => recordedRows.filter((row) => isTempWorkSessionEditable(row, getPaidThrough(row))),
    [recordedRows, paidThroughByWorkerId],
  )

  useEffect(() => {
    setSelectedSessionIds(new Set())
  }, [workDate, locationFilter])

  const selectedCount = selectedSessionIds.size
  const allEditableSelected =
    editableRows.length > 0 && editableRows.every((row) => selectedSessionIds.has(row.id))
  const someEditableSelected = editableRows.some((row) => selectedSessionIds.has(row.id))

  const toggleSessionSelection = (sessionId: string, checked: boolean) => {
    setSelectedSessionIds((prev) => {
      const next = new Set(prev)
      if (checked) next.add(sessionId)
      else next.delete(sessionId)
      return next
    })
  }

  const toggleSelectAllEditable = (checked: boolean) => {
    if (!checked) {
      setSelectedSessionIds(new Set())
      return
    }
    setSelectedSessionIds(new Set(editableRows.map((row) => row.id)))
  }

  const openBulkDialog = (sessions: TempWorkSession[]) => {
    if (sessions.length === 0) return
    setBulkDialogSessions(sessions)
    setBulkDialogOpen(true)
  }

  const onBulkEditSuccess = ({ newWorkDate, updatedCount }: { newWorkDate: string; updatedCount: number }) => {
    if (updatedCount > 0 && newWorkDate !== workDate) {
      const movedIds = new Set(bulkDialogSessions.map((s) => s.id))
      const remaining = recordedRows.filter((row) => !movedIds.has(row.id))
      if (remaining.length === 0) {
        setWorkDate(newWorkDate)
      }
    }
    setSelectedSessionIds(new Set())
    void refetchSessions()
    void refetchWorkers()
  }

  const recordedTotal =
    sessionsRes?.data.total_pay_baht ??
    recordedRows.reduce((sum, r) => sum + (r.pay_baht != null ? Number(r.pay_baht) : 0), 0)

  const peopleBadge =
    locale === 'th'
      ? `${recordedRows.length} ${t('people_unit')}`
      : `${recordedRows.length} ${recordedRows.length === 1 ? t('person_unit') : t('people_plural')}`

  const formatSessionDateTime = (iso: string) =>
    new Date(iso).toLocaleString(locale === 'th' ? 'th-TH' : 'en-US', {
      dateStyle: 'short',
      timeStyle: 'medium',
    })

  const workDateValue = useMemo(() => parseWorkDateString(workDate), [workDate])

  return (
    <div className="w-full min-w-0 max-w-none space-y-8 pb-10">
      {/* Hero + filters */}
      <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100 md:text-3xl">
            {t('hero_title')}
          </h1>
          <p className="mt-1 max-w-xl text-sm text-muted-foreground md:text-base">{t('hero_subtitle')}</p>
        </div>

        <CardShell className="shrink-0 p-4 lg:min-w-[320px]">
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
            <div className="min-w-[9rem] flex-1">
              <Label htmlFor="tw-date" className="text-xs text-muted-foreground">
                {t('work_date')}
              </Label>
              <DatePicker
                id="tw-date"
                value={workDateValue}
                onChange={(d) => {
                  if (d) setWorkDate(toWorkDateString(d))
                }}
                className="mt-1.5 h-10 w-full justify-between"
                align="start"
              />
            </div>
            <div className="min-w-[10rem] flex-1">
              <Label className="text-xs text-muted-foreground">{t('location_filter')}</Label>
              <Select
                value={locationFilter || '__all__'}
                onValueChange={(v) => setLocationFilter(v === '__all__' ? '' : v)}
              >
                <SelectTrigger className="mt-1.5 h-10">
                  <SelectValue placeholder={t('all_locations')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">{t('all_locations')}</SelectItem>
                  {BILL_LOCATION_VALUES.map((loc) => (
                    <SelectItem key={loc} value={loc}>
                      {BILL_LOCATION_LABELS[loc]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              type="button"
              variant="outline"
              className="h-10 gap-2 border-blue-200 bg-blue-50/80 text-blue-900 hover:bg-blue-100 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-100 dark:hover:bg-blue-950"
              onClick={() => {
                refetchSessions()
                refetchWorkers()
              }}
            >
              <Icon icon="mdi:refresh" className="size-4" />
              {t('refresh')}
            </Button>
          </div>
        </CardShell>
      </div>

      <div className="grid gap-6 lg:grid-cols-5 lg:items-start">
        {/* Left column — attendance */}
        <div className="space-y-6 lg:col-span-3">
          <CardShell className="p-5 md:p-6">
            <SectionTitle accent="blue">{t('record_work_section')}</SectionTitle>
            <form onSubmit={onRecordWork} className="mt-5 space-y-4">
              <div>
                <Label htmlFor="tw-name" className="text-sm text-muted-foreground">
                  {t('worker_name')}
                </Label>
                <div className="mt-1.5">
                  <WorkerNameCombobox
                    id="tw-name"
                    workers={activeWorkers}
                    value={recordName}
                    onChange={setRecordName}
                    placeholder={t('name_placeholder')}
                    searchPlaceholder={t('search_workers')}
                    fromListHeading={t('from_saved_list')}
                    listEmptyMessage={t('worker_list_empty')}
                    formatCustomNameLabel={(name) => t('use_custom_name', { name })}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="tw-record-date" className="text-sm text-muted-foreground">
                  {t('record_work_date_label')}
                </Label>
                <DatePicker
                  id="tw-record-date"
                  value={workDateValue}
                  onChange={(d) => {
                    if (d) setWorkDate(toWorkDateString(d))
                  }}
                  className="mt-1.5 h-11 w-full justify-between"
                  align="start"
                />
                <p className="mt-1.5 text-xs text-muted-foreground">{t('record_work_date_hint')}</p>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="tw-record-pay" className="text-sm text-muted-foreground">
                    {t('pay_label')}
                  </Label>
                  <Input
                    id="tw-record-pay"
                    type="number"
                    min={0}
                    step="0.01"
                    value={recordPay}
                    onChange={(e) => setRecordPay(e.target.value)}
                    className="mt-1.5 h-11"
                  />
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">{t('session_location')}</Label>
                  <Select
                    value={recordLocation || '__none__'}
                    onValueChange={(v) => setRecordLocation(v === '__none__' ? '' : v)}
                  >
                    <SelectTrigger className="mt-1.5 h-11">
                      <SelectValue placeholder={t('optional')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">{t('optional')}</SelectItem>
                      {BILL_LOCATION_VALUES.map((loc) => (
                        <SelectItem key={loc} value={loc}>
                          {BILL_LOCATION_LABELS[loc]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label htmlFor="tw-record-note" className="text-sm text-muted-foreground">
                  {t('note_label')}
                </Label>
                <Textarea
                  id="tw-record-note"
                  className="mt-1.5 min-h-[88px] resize-y"
                  value={recordNote}
                  onChange={(e) => setRecordNote(e.target.value)}
                  placeholder={t('note_placeholder')}
                />
              </div>
              <PrimaryBarButton
                type="submit"
                icon="mdi:clipboard-check-outline"
                disabled={$recordWork.isLoading}
              >
                {t('submit_record_work')}
              </PrimaryBarButton>
            </form>
          </CardShell>


          <CardShell>
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/60 bg-muted/20 px-5 py-3 md:px-6">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold">{t('recorded_sessions')}</span>
                <span className="rounded-full bg-blue-100 px-3 py-0.5 text-xs font-semibold text-blue-800 dark:bg-blue-950 dark:text-blue-200">
                  {peopleBadge}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {editableRows.length > 0 ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8"
                    onClick={() => openBulkDialog(editableRows)}
                  >
                    {t('bulk_move_all_day')}
                  </Button>
                ) : null}
                {selectedCount > 0 ? (
                  <Button
                    type="button"
                    size="sm"
                    className="h-8"
                    onClick={() =>
                      openBulkDialog(
                        recordedRows.filter((row) => selectedSessionIds.has(row.id)),
                      )
                    }
                  >
                    {t('bulk_change_date', { count: selectedCount })}
                  </Button>
                ) : null}
              </div>
            </div>
            {sessionsLoading ? (
              <div className="p-6 text-sm text-muted-foreground">{t('loading')}</div>
            ) : recordedRows.length === 0 ? (
              <div className="p-6 text-sm text-muted-foreground">{t('empty_recorded')}</div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[920px] text-sm">
                    <thead>
                      <tr className="border-b border-border/60 bg-muted/30 text-left text-muted-foreground">
                        <th className="w-10 px-3 py-2.5 font-medium md:px-4">
                          <Checkbox
                            checked={
                              allEditableSelected
                                ? true
                                : someEditableSelected
                                  ? 'indeterminate'
                                  : false
                            }
                            disabled={editableRows.length === 0}
                            onCheckedChange={(checked) =>
                              toggleSelectAllEditable(checked === true)
                            }
                            aria-label={t('select_all_editable')}
                          />
                        </th>
                        <th className="px-5 py-2.5 font-medium md:px-6">{t('worker_name')}</th>
                        <th className="px-3 py-2.5 font-medium">{t('col_position')}</th>
                        <th className="px-3 py-2.5 font-medium">{t('recorded_at')}</th>
                        <th className="max-w-[200px] px-3 py-2.5 font-medium">{t('col_note')}</th>
                        <th className="px-5 py-2.5 text-right font-medium md:px-6">{t('pay')}</th>
                        <th className="px-5 py-2.5 text-right font-medium md:px-6">{t('col_actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recordedRows.map((row) => {
                        const paidThrough = getPaidThrough(row)
                        const editable = isTempWorkSessionEditable(row, paidThrough)
                        return (
                        <tr key={row.id} className="border-b border-border/40 last:border-0">
                          <td className="px-3 py-3 md:px-4">
                            <Checkbox
                              checked={selectedSessionIds.has(row.id)}
                              disabled={!editable}
                              onCheckedChange={(checked) =>
                                toggleSessionSelection(row.id, checked === true)
                              }
                              aria-label={t('col_select')}
                              title={!editable ? t('locked_paid') : undefined}
                            />
                          </td>
                          <td className="px-5 py-3 font-medium md:px-6">
                            {row.temp_worker_id ? (
                              <Link
                                to="/admin/temp-workers/$id"
                                params={{ id: row.temp_worker_id }}
                                className="hover:text-primary hover:underline"
                              >
                                {row.worker_name_snapshot}
                              </Link>
                            ) : (
                              row.worker_name_snapshot
                            )}
                          </td>
                          <td className="px-3 py-3 text-muted-foreground">
                            {row.worker_position_snapshot ?? '—'}
                          </td>
                          <td className="px-3 py-3 text-muted-foreground">
                            {row.check_out_at
                              ? formatSessionDateTime(row.check_out_at)
                              : formatSessionDateTime(row.check_in_at)}
                          </td>
                          <td className="max-w-[200px] px-3 py-3 text-muted-foreground">
                            <span className="line-clamp-2 whitespace-pre-wrap break-words">
                              {row.note?.trim() ? row.note : t('no_note')}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-right font-bold text-blue-600 dark:text-blue-400 md:px-6">
                            {row.pay_baht != null ? `${formatPrice(row.pay_baht)} ${t('baht')}` : '—'}
                          </td>
                          <td className="px-5 py-3 text-right md:px-6">
                            <TempWorkSessionRowActions
                              session={row}
                              paidThroughWorkDate={paidThrough}
                              compact
                              onSuccess={(kind, next) => {
                                if (kind === 'update') {
                                  setWorkDate(next.work_date)
                                  setLocationFilter(next.location ?? '')
                                }
                                void refetchSessions()
                                void refetchWorkers()
                              }}
                            />
                          </td>
                        </tr>
                      )})}
                    </tbody>
                  </table>
                </div>
                <BulkEditWorkDateDialog
                  open={bulkDialogOpen}
                  onOpenChange={setBulkDialogOpen}
                  sessions={bulkDialogSessions}
                  currentWorkDate={workDate}
                  onSuccess={onBulkEditSuccess}
                />
                <div className="flex items-center justify-between border-t border-border/60 bg-muted/25 px-5 py-3 text-sm font-semibold md:px-6">
                  <span>{t('total_all')}</span>
                  <span className="text-base font-bold text-blue-600 dark:text-blue-400">
                    {formatPrice(recordedTotal)} {t('baht')}
                  </span>
                </div>
              </>
            )}
          </CardShell>
        </div>

        {/* Right column — master + stats */}
        <div className="space-y-6 lg:col-span-2">
          <CardShell className="p-5 md:p-6">
            <SectionTitle accent="slate">{t('master_section')}</SectionTitle>
            <form onSubmit={onAddMaster} className="mt-5 space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Label htmlFor="tw-new-name" className="text-sm text-muted-foreground">
                    {t('new_master_name')}
                  </Label>
                  <Input
                    id="tw-new-name"
                    value={newMasterName}
                    onChange={(e) => setNewMasterName(e.target.value)}
                    className="mt-1.5 h-11"
                  />
                </div>
                <div>
                  <Label htmlFor="tw-new-position" className="text-sm text-muted-foreground">
                    {t('new_master_position')}
                  </Label>
                  <Input
                    id="tw-new-position"
                    value={newMasterPosition}
                    onChange={(e) => setNewMasterPosition(e.target.value)}
                    className="mt-1.5 h-11"
                  />
                </div>
                <div>
                  <Label htmlFor="tw-new-phone" className="text-sm text-muted-foreground">
                    {t('new_master_phone')}
                  </Label>
                  <Input
                    id="tw-new-phone"
                    value={newMasterPhone}
                    onChange={(e) => setNewMasterPhone(e.target.value)}
                    className="mt-1.5 h-11"
                  />
                </div>
              </div>
              <PrimaryBarButton type="submit" icon="mdi:account-plus" disabled={$createWorker.isLoading}>
                {t('add_master')}
              </PrimaryBarButton>
            </form>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <p className="text-sm font-medium text-muted-foreground">{t('current_list')}</p>
              <form
                className="flex w-full max-w-md gap-2 sm:w-auto"
                onSubmit={(e) => {
                  e.preventDefault()
                  setMasterSearch(masterSearchInput)
                }}
              >
                <Input
                  value={masterSearchInput}
                  onChange={(e) => setMasterSearchInput(e.target.value)}
                  placeholder={t('search_master_list')}
                  className="h-10"
                />
                <Button type="submit" variant="secondary" className="h-10 shrink-0">
                  <Icon icon="mdi:magnify" className="size-4" />
                </Button>
              </form>
            </div>
            <div className="mt-3 overflow-x-auto rounded-lg border border-border/50">
              <table className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="border-b border-border/60 bg-muted/30 text-left text-muted-foreground">
                    <th className="px-4 py-2.5 font-medium">{t('worker_name')}</th>
                    <th className="px-3 py-2.5 font-medium">{t('col_position')}</th>
                    <th className="px-3 py-2.5 font-medium">{t('new_master_phone')}</th>
                    <th className="px-3 py-2.5 font-medium">{t('col_status')}</th>
                    <th className="px-4 py-2.5 text-right font-medium">{t('col_actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {workersLoading ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                        {t('loading')}
                      </td>
                    </tr>
                  ) : masterRows.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                        {t('empty_master_list')}
                      </td>
                    </tr>
                  ) : (
                    masterRows.map((w) => (
                      <tr key={w.id} className="border-b border-border/40 last:border-0">
                        <td className="px-4 py-3">
                          <div className="flex min-w-0 items-center gap-2">
                            <div
                              className={cn(
                                'flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-bold',
                                w.active ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground',
                              )}
                            >
                              {avatarInitial(w.name)}
                            </div>
                            <Link
                              to="/admin/temp-workers/$id"
                              params={{ id: w.id }}
                              className={cn(
                                'truncate font-medium hover:text-primary hover:underline',
                                !w.active && 'text-muted-foreground line-through',
                              )}
                            >
                              {w.name}
                            </Link>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-muted-foreground">{w.position ?? '—'}</td>
                        <td className="px-3 py-3 tabular-nums text-muted-foreground">{w.phone ?? '—'}</td>
                        <td className="px-3 py-3">
                          <span
                            className={cn(
                              'inline-flex whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium',
                              w.active
                                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200'
                                : 'bg-muted text-muted-foreground',
                            )}
                          >
                            {w.active ? t('status_active') : t('status_inactive')}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap items-center justify-end gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-8 gap-1 border-primary/30 text-primary hover:bg-primary/5"
                              asChild
                            >
                              <Link
                                to="/admin/temp-workers/$id"
                                params={{ id: w.id }}
                                title={t('view_profile')}
                              >
                                <Icon icon="mdi:account-eye-outline" className="size-4" aria-hidden />
                                <span className="hidden sm:inline">{t('view_profile')}</span>
                              </Link>
                            </Button>
                            {w.active ? (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-8"
                                onClick={async () => {
                                  try {
                                    await $updateWorker.mutateAsync({ id: w.id, active: false })
                                    toast.toastSuccess(t('deactivated'))
                                    refetchWorkers()
                                  } catch (e: any) {
                                    toast.toastFailed(e?.message || t('update_fail'))
                                  }
                                }}
                              >
                                {t('deactivate')}
                              </Button>
                            ) : (
                              <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                className="h-8"
                                onClick={async () => {
                                  try {
                                    await $updateWorker.mutateAsync({ id: w.id, active: true })
                                    toast.toastSuccess(t('reactivated'))
                                    refetchWorkers()
                                  } catch (e: any) {
                                    toast.toastFailed(e?.message || t('update_fail'))
                                  }
                                }}
                              >
                                {t('reactivate')}
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-sm">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={masterCurrentPage <= 1}
                onClick={() => setMasterPage((p) => p - 1)}
              >
                {tButtons('previous')}
              </Button>
              <span className="text-muted-foreground">
                {t('page_indicator', { current: masterCurrentPage, total: masterTotalPages })}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={masterCurrentPage >= masterTotalPages}
                onClick={() => setMasterPage((p) => p + 1)}
              >
                {tButtons('next')}
              </Button>
            </div>
          </CardShell>

          <div className="relative overflow-hidden rounded-xl border border-slate-700/50 shadow-md">
            <div className="absolute inset-0 bg-gradient-to-br from-slate-800 via-slate-900 to-slate-950" />
            <div className="absolute inset-0 opacity-30 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-500/40 via-transparent to-transparent" />
            <div className="absolute -right-8 -top-12 size-40 rounded-full bg-blue-500/20 blur-2xl" />
            <div className="relative px-6 py-10 text-white">
              <p className="text-lg font-semibold tracking-tight">{t('stats_title')}</p>
              <p className="mt-2 max-w-xs text-sm leading-relaxed text-slate-300">{t('stats_subtitle')}</p>
            </div>
          </div>
        </div>
      </div>

    </div>
  )
}
